/**
 * Global confirm-dialog provider with a styled destructive-action dialog.
 * Drop-in replacement for window.confirm() with much nicer UX.
 *
 * Usage:
 *   const confirm = useConfirm();
 *
 *   // Simple — returns a Promise<boolean>
 *   if (!(await confirm({
 *       title: 'Delete this website?',
 *       description: 'Pageview data is preserved but the site stops collecting.',
 *       confirmLabel: 'Delete website',
 *   }))) return;
 *   await axios.delete(...);
 *
 *   // With in-dialog loading — pass `task` and the dialog stays open with a spinner
 *   await confirm({
 *       title: 'Remove team member?',
 *       description: <>...</>,
 *       confirmLabel: 'Remove',
 *       icon: UserMinus,
 *       task: async () => { await axios.delete(...); refetch(); },
 *   });
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from "react";
import { Trash2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogContent,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(() => Promise.resolve(false));

export function useConfirm() {
    return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null); // { title, description, ... } or null
    const [loading, setLoading] = useState(false);
    const resolverRef = useRef(null);

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setState(options || {});
        });
    }, []);

    function close(result) {
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
        setState(null);
        setLoading(false);
    }

    async function handleConfirm() {
        if (typeof state?.task === "function") {
            setLoading(true);
            try {
                await state.task();
                close(true);
            } catch {
                // Caller is responsible for surfacing errors via toast etc.
                close(false);
            }
        } else {
            close(true);
        }
    }

    const Icon = state?.icon || Trash2;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            <AlertDialog
                open={!!state}
                onOpenChange={(open) => {
                    if (!open && !loading) close(false);
                }}
            >
                <AlertDialogContent
                    className="max-w-sm p-0 overflow-hidden rounded-[28px] border-0 shadow-2xl ring-8 ring-white/60"
                >
                    {/* Hero band — pastel gradient with icon badge */}
                    <div className="relative bg-gradient-to-br from-rose-50 via-red-50 to-orange-50 px-6 py-10 flex items-center justify-center">
                        <div className="absolute inset-0 opacity-60 pointer-events-none">
                            <div className="absolute -top-4 -left-4 w-24 h-24 bg-rose-200/40 rounded-full blur-2xl" />
                            <div className="absolute -bottom-4 -right-4 w-28 h-28 bg-orange-200/40 rounded-full blur-2xl" />
                        </div>
                        <div className="relative w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                            <Icon className="w-10 h-10 text-rose-500" strokeWidth={1.5} />
                        </div>
                    </div>

                    {/* Body */}
                    <div className="px-7 pt-6 pb-7 bg-white text-center">
                        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                            {state?.title || "Are you sure?"}
                        </h2>
                        {state?.description && (
                            <div className="mt-3 text-sm text-gray-500 leading-relaxed">
                                {state.description}
                            </div>
                        )}

                        <div className="mt-7 flex gap-3">
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => close(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {state?.cancelLabel || "Cancel"}
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleConfirm}
                                className="flex-1 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-medium shadow-sm shadow-rose-500/30 transition-colors disabled:opacity-50"
                            >
                                {loading ? "Working…" : state?.confirmLabel || "Delete"}
                            </button>
                        </div>
                    </div>
                </AlertDialogContent>
            </AlertDialog>
        </ConfirmContext.Provider>
    );
}
